import type { IIndexedSelectionItem } from "../types";
import type {
  IStripSelectionsProps,
  TWithWireSelections,
} from "./send-message-payload.types";
import {
  toWireRefusedSkillNames,
  toWireSkillSelectionUuids,
} from "./skill-selection";

// NOTE: Reduce one selection array to the wire-minimal `{ uuid, index }` shape.
// Nullish entries are dropped rather than dereferenced, so a malformed array can
// never throw before the socket emits — mirroring the backend's defensive
// `normalizeIndexedSelection`, which likewise discards non-record entries.
const toWireSelections = (
  selections: readonly IIndexedSelectionItem[],
): IIndexedSelectionItem[] =>
  selections
    .filter((selection) => selection != null)
    .map((selection) => ({ uuid: selection.uuid, index: selection.index }));

// NOTE: NSAI-4063 — reduce the `models` / `assistants` selections to the only
// fields the server consumes (`uuid`, `index`) before emitting `sendMessage`.
// The client selections otherwise carry heavy display data — notably assistant
// `avatar`, which is a base64 data URI up to ~680KB each. In a multi-assistant
// chat that inflated every send to multiple MB of dead weight, and because the
// ack-timeout heuristic only accounts for attached files (not this bloat) the
// oversized frame would miss its ack, force a reconnect, and loop the sender on
// "Reconnecting to the server…". Stripping keeps the wire frame small; all local
// state (optimistic UI, retry buffer) is left untouched by callers, and the
// optimistic renderers key off `uuid` + separate meta maps, not these fields.
export const stripSelectionsForWire = <TPayload extends IStripSelectionsProps>(
  payload: TPayload,
): TWithWireSelections<TPayload> => {
  // NOTE: Guard with Array.isArray so a truthy non-array never reaches `.map`;
  // a malformed value becomes `undefined` (dropped from the wire) rather than
  // passing through — the server treats a missing field as no selection, the
  // same result it reaches by normalizing a malformed one.
  return {
    ...payload,
    models: Array.isArray(payload.models)
      ? toWireSelections(payload.models)
      : undefined,
    assistants: Array.isArray(payload.assistants)
      ? toWireSelections(payload.assistants)
      : undefined,
    // NOTE: NSAI-3308 — normalized at the one place every send passes through,
    // including the buffered reconnect replay, so a retry pins exactly the
    // skills the original attempt pinned.
    skillIds: toWireSkillSelectionUuids(payload.skillIds),
    // NOTE: NSAI-5925 — the refusal record rides the same normalization pass, so
    // a retry states the same refusals rather than none.
    refusedSkillNames: toWireRefusedSkillNames(payload.refusedSkillNames),
  };
};
